import { ApolloClient, InMemoryCache, useApolloClient } from '@apollo/client';
import {
  type ActionHandler,
  Create,
  GenericField,
  type Nullable,
  type ObjectSchemaDefinition,
  SelectField,
  SingleLineTextField,
  type StationMessage,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { debounce } from 'debounce';
import { Field, type FormikValues, useFormikContext } from 'formik';
import generator from 'generate-password-browser';
import { PasswordPolicy, charsets } from 'password-sheriff';
import { generateSlug } from 'random-word-slugs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import type { OptionalObjectSchema } from 'yup/lib/object';
import {
  type CreateEnvironmentMutation,
  type EnvironmentInput,
  type EnvironmentTemplateDataQuery,
  useCreateEnvironmentMutation,
  useEnvironmentTemplateDataQuery,
} from '../../../../generated/id-service-env-administration';
import {
  CheckSubdomainAvailabilityDocument,
  type CheckSubdomainAvailabilityQuery,
  type CheckSubdomainAvailabilityQueryVariables,
  SubdomainAvailableResult,
} from '../../../../generated/portal-service';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { authStateStore } from '../../../../state-store';
import classes from './EnvironmentCreate.module.scss';

interface FormData {
  name: string;
  environmentTemplateId: string;
  managementSystemAdminEmail?: string;
  managementSystemAdminPassword?: string;
  managementSystemSubdomain?: string;
}

/**
 * If a new item is added to this array or an existing item removed from this, the corresponding environmentTemplateId must be updated via a DB Migration as this object is simply a mapping between ID & description.
 */
const envTemplateDescriptions = [
  {
    id: 'BASIC',
    description: `Enable only the core-services for the new environment. This would allow to pick and choose the exact services as necessary once the environment is initialized.

Enabled Managed Services
========================

Hosting Service
Identity Service
Management System Service
Microfrontend Service`,
  },
  {
    id: 'DEV',
    description:
      'This template will enable all managed-services and setup an environment to be used for development purposes. The input you provide below will be used to pre-configure some service with defaults.',
  },
  {
    id: 'INTERNAL_DEV',
    description: 'Used during local development. Work in progress!',
  },
];

// Changes to the password policy should be reflected in password-policy validation error message below
const passwordPolicy = new PasswordPolicy({
  length: { minLength: 10 },
  containsAtLeast: {
    atLeast: 3,
    expressions: [
      charsets.lowerCase,
      charsets.upperCase,
      charsets.numbers,
      charsets.specialCharacters,
    ],
  },
});

const devTemplateId = 'DEV';

type SubmitResponse = Nullable<CreateEnvironmentMutation['createEnvironment']>;

export const EnvironmentCreate: React.FC = () => {
  const apolloClient = useApolloClient();
  const portalApolloClient = useRef<ApolloClient<unknown>>();
  const [stationMessage, setStationMessage] = useState<StationMessage>();
  // Initialize ApolloClient for portal-service
  useEffect(() => {
    (async () => {
      portalApolloClient.current = new ApolloClient({
        cache: new InMemoryCache(),
        uri: () => {
          return new URL(
            `/graphql`,
            config.portalServiceAdminBaseUrl,
          ).toString();
        },
      });
    })();
  }, []);

  const checkSubdomainAvailability = async (
    subdomain: string,
    apolloClient: ApolloClient<unknown>,
    callback: (isSubdomainAvailable: boolean) => void,
  ): Promise<void> => {
    try {
      const response = await apolloClient.query<
        CheckSubdomainAvailabilityQuery,
        CheckSubdomainAvailabilityQueryVariables
      >({
        query: CheckSubdomainAvailabilityDocument,
        variables: {
          input: {
            subdomain,
          },
        },
        fetchPolicy: 'no-cache',
      });
      callback(
        response?.data.checkSubdomainAvailability?.result ===
          SubdomainAvailableResult.AvailableForUse,
      );
    } catch (error) {
      const apolloError = error as {
        networkError?: { message: string };
        name?: string;
      };
      if (apolloError.name === 'ApolloError' && apolloError.networkError) {
        setStationMessage({
          title: 'A network error occurred',
          body: (
            <>
              <p>
                Unable to access the Management System Service. Please check
                your network connection and try again.
              </p>
            </>
          ),
        });
      }
    }
  };

  const checkSubdomainAvailabilityDebounced = debounce(
    checkSubdomainAvailability,
    500,
    false,
  );

  const getEnvironmentCreateSchema = (
    apolloClient: ApolloClient<unknown> | undefined,
  ): OptionalObjectSchema<ObjectSchemaDefinition<FormData>> => {
    return Yup.object<ObjectSchemaDefinition<FormData>>({
      name: Yup.string().label('Name').required().max(255),
      environmentTemplateId: Yup.string()
        .label('Template ID')
        .required()
        .test('template-id', '', function (value: string | undefined) {
          if (value !== devTemplateId) {
            setStationMessage(undefined);
          }
          return true;
        }),
      managementSystemAdminEmail: Yup.string()
        .label('Management System Admin Email')
        .email()
        .max(255)
        .when('environmentTemplateId', {
          is: devTemplateId,
          then: Yup.string().required(),
        }),
      managementSystemAdminPassword: Yup.string()
        .label('Management System Admin Password')
        .min(10)
        .max(32)
        .when('environmentTemplateId', {
          is: devTemplateId,
          then: Yup.string()
            .required()
            .test(
              'password-policy',
              // Changes to the following error message should be reflected in the password policy above
              'Management System Admin Password should contain at least 3 of the following 4 types of characters: lower case letters, upper case letters, numbers and special characters',
              function (value: string | undefined) {
                return value !== undefined && passwordPolicy.check(value);
              },
            ),
        }),
      managementSystemSubdomain: Yup.string()
        .label('Management System Subdomain')
        .min(3)
        .max(63)
        .matches(
          /^(([a-z0-9]|[a-z0-9][a-z0-9\\-]*[a-z0-9]))$/,
          "Invalid subdomain format. Subdomain cannot contain any uppercase, or special characters except '-'",
        )
        .when('environmentTemplateId', {
          is: devTemplateId,
          then: Yup.string()
            .required()
            .test(
              'management-system-subdomain-availability',
              'Management System Subdomain is not available for use',
              async (subdomain) =>
                new Promise((resolve) => {
                  if (
                    subdomain !== undefined &&
                    subdomain.length > 0 &&
                    apolloClient !== undefined
                  ) {
                    // Check the availability of Management System Subdomain
                    // It uses a backend call which will be executed for each key press by default
                    // Hence a debouncer is used here to reduce the number of back end calls
                    checkSubdomainAvailabilityDebounced(
                      subdomain,
                      apolloClient,
                      resolve,
                    );
                  } else {
                    resolve(false);
                  }
                }),
            ),
        }),
    });
  };

  const [environmentCreate] = useCreateEnvironmentMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const { loading, data, error } = useEnvironmentTemplateDataQuery({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const saveData = useCallback(
    async (formData: FormikValues): Promise<SubmitResponse> => {
      let initDefaults: EnvironmentInput['initDefaults'] = undefined;
      if (formData.environmentTemplateId === devTemplateId) {
        // Check the availability of Management System Subdomain
        if (portalApolloClient.current !== undefined) {
          await checkSubdomainAvailability(
            formData.managementSystemSubdomain,
            portalApolloClient.current,
            (isSubdomainAvailable) => {
              if (isSubdomainAvailable === false) {
                throw new Error(
                  'Management System Subdomain is not available for use',
                );
              }
            },
          );
        }

        initDefaults = {
          portal_service: {
            portal_sub_domain: formData.managementSystemSubdomain,
          },
          id_service: {
            management_super_admins: [formData.managementSystemAdminEmail],
          },
          ax_auth_service: {
            enabled: true,
            users: [
              {
                first_name: 'Developer',
                last_name: 'Account',
                email: formData.managementSystemAdminEmail,
                password: formData.managementSystemAdminPassword,
              },
            ],
          },
        };
      }

      const response = await environmentCreate({
        variables: {
          input: {
            environment: {
              name: formData.name,
              environmentTemplateId: formData.environmentTemplateId,
              initDefaults,
              isDevelopment: formData.environmentTemplateId === devTemplateId,
            },
          },
        },
      });
      return response.data?.createEnvironment ?? null;
    },
    [environmentCreate],
  );

  const history = useHistory();
  const onProceed = useCallback<ActionHandler<FormData, SubmitResponse>>(
    ({ submitResponse }) => {
      if (submitResponse?.environment) {
        if (
          submitResponse.environment.environmentTemplateId === devTemplateId
        ) {
          history.push(
            `/env/${submitResponse.environment.id}/environmentConnection`,
          );
        } else {
          history.push(`/env/${submitResponse.environment.id}`);
        }
      } else {
        // The schema has the response.data properties marked as optional, since theoretically a user could have
        // permissions to mutate but not to read. In practice this can not happen on that service, so we just throw
        // an error in case we get there.
        throw new Error('Not expected');
      }
    },
    [history],
  );

  return (
    <Create<FormData, SubmitResponse>
      title="New Environment"
      validationSchema={getEnvironmentCreateSchema(portalApolloClient.current)}
      saveData={saveData}
      onProceed={onProceed}
      cancelNavigationUrl={'/env'}
      stationMessage={stationMessage}
      initialData={{
        data: {
          name: '',
          // The form will automatically reinitialize whenever the data updates/arrives
          environmentTemplateId: data?.environmentTemplates?.nodes[0]?.id ?? '',
        },
        loading,
        error: error?.message,
      }}
    >
      <Form templates={data?.environmentTemplates?.nodes} />
    </Create>
  );
};
const Form: React.FC<{
  templates?: NonNullable<
    EnvironmentTemplateDataQuery['environmentTemplates']
  >['nodes'];
}> = ({ templates }) => {
  const { values, setValues } = useFormikContext<FormData>();
  const [templateDescription, setTemplateDescription] = useState('');
  const tenantAdminEmail = authStateStore.useState('tenantAdminEmail');

  // Update templateDescription when templateId changes
  useEffect(() => {
    setTemplateDescription(
      envTemplateDescriptions?.find(
        (template) => template.id === values.environmentTemplateId,
      )?.description ?? '',
    );
  }, [templates, values.environmentTemplateId]);

  // Set default values for fields that are specific to development template
  useEffect(() => {
    const generateRandomPassword = (): string => {
      return generator.generate({
        length: 12,
        numbers: true,
        lowercase: true,
        uppercase: true,
        strict: true,
      });
    };

    const generatePortalSubDomain = (): string => {
      const randomString = generateSlug(2, {
        format: 'kebab',
        partsOfSpeech: ['adjective', 'noun'],
        categories: {
          adjective: ['time', 'color', 'condition', 'shapes', 'quantity'],
          noun: [
            'time',
            'people',
            'education',
            'business',
            'thing',
            'transportation',
            'technology',
            'media',
            'health',
            'sports',
            'science',
          ],
        },
      });
      const randomNumber = Math.floor(1000 + Math.random() * 9000);
      return `${randomString}-${randomNumber}`;
    };

    if (values.environmentTemplateId === devTemplateId) {
      setValues({
        ...values,
        managementSystemAdminEmail: tenantAdminEmail,
        managementSystemAdminPassword: generateRandomPassword(),
        managementSystemSubdomain: generatePortalSubDomain(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.environmentTemplateId]);

  return (
    <>
      <Field name="name" label="Name" as={SingleLineTextField} />
      <Field
        name="environmentTemplateId"
        label="Template Name"
        as={SelectField}
        options={templates?.map((template) => ({
          value: template.id,
          label: template.name,
        }))}
      />
      <GenericField name="templateDescription" label="">
        <div className={classes.templateDescription}>{templateDescription}</div>
      </GenericField>
      {values.environmentTemplateId === devTemplateId && (
        <>
          <Field
            name="managementSystemAdminEmail"
            label="Management System Admin Email"
            as={SingleLineTextField}
          />
          <Field
            name="managementSystemAdminPassword"
            label="Management System Admin Password"
            as={SingleLineTextField}
          />
          <Field
            name="managementSystemSubdomain"
            label="Management System Subdomain"
            as={SingleLineTextField}
          />
        </>
      )}
    </>
  );
};

export const EnvironmentCreateCrumb: BreadcrumbResolver = () =>
  'New Environment';
